import { useEffect, useState } from 'react';
import { getWorkPolicy } from '../../api/hr-schedule';
import { tCalendar } from '../../lib/calendar-i18n';
import type { CalendarEventType } from '../../api/calendar';
import { displayHour12, pad2 } from './time-label';
import { ALL_DAY_FALLBACK, HHMM } from './all-day-work-hours';

/**
 * ⭐ 2026-10-20 — 일정 창 «유형별 추천 시간» (시안 E · 사용자 결정). 달력 창(EventRangePicker) 시작·종료 칸 아래 칩 줄.
 *
 * ```
 * 회의   30분 · 1시간 · 1시간 30분 · 2시간     시작은 그대로, 끝 = 시작 + 길이
 * 외부   오전(출근 ~ 12:00) · 오후(13:00 ~ 퇴근)  그 날짜의 «내 근무시간»으로 나눈다(12~13시는 점심으로 비운다)
 * 휴가   반차 · 반반차 · 연차                   반차·반반차는 «시작만» 고르고 끝은 시작 + 길이로 잠근다
 * ```
 * 휴가 길이·범위는 근태의 반차 규칙 그대로다(`/hr/attendance/policy` 의 `partialLeave`) — 소정근로 ÷ 2 · ÷ 4,
 *   시작 ≥ 출근 · 끝 ≤ 퇴근. 근무 정책을 못 읽으면 09:00 ~ 18:00 · 4시간 · 2시간.
 */

export type VacationPart = 'half' | 'quarter';

export interface DayWork {
  start: string;
  end: string;
  earliestStart: string;
  latestEnd: string;
  halfMinutes: number;
  quarterMinutes: number;
}

export const DAY_WORK_FALLBACK: DayWork = {
  start: ALL_DAY_FALLBACK.start, end: ALL_DAY_FALLBACK.end,
  earliestStart: ALL_DAY_FALLBACK.start, latestEnd: ALL_DAY_FALLBACK.end,
  halfMinutes: 240, quarterMinutes: 120,
};

const MEETING_LENGTHS = [30, 60, 90, 120] as const;
const NOON = 12 * 60;
const AFTER_LUNCH = 13 * 60;
const minOf = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const hhmmOf = (m: number) => `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;

/** 그 날짜의 내 근무시간 · 반차 재료. 못 읽으면 기본값. `enabled` 가 거짓이면 부르지 않는다. */
export function useDayWork(date: string, enabled: boolean): DayWork {
  const [work, setWork] = useState<DayWork>(DAY_WORK_FALLBACK);
  useEffect(() => {
    if (!enabled || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    let alive = true;
    getWorkPolicy(date)
      .then((p) => {
        if (!alive) return;
        if (!p || !HHMM.test(p.baseStart ?? '') || !HHMM.test(p.baseEnd ?? '')) { setWork(DAY_WORK_FALLBACK); return; }
        const pl = p.partialLeave;
        setWork({
          start: p.baseStart,
          end: p.baseEnd,
          earliestStart: pl && HHMM.test(pl.earliestStart) ? pl.earliestStart : p.baseStart,
          latestEnd: pl && HHMM.test(pl.latestEnd) ? pl.latestEnd : p.baseEnd,
          halfMinutes: pl && pl.halfMinutes > 0 ? pl.halfMinutes : DAY_WORK_FALLBACK.halfMinutes,
          quarterMinutes: pl && pl.quarterMinutes > 0 ? pl.quarterMinutes : DAY_WORK_FALLBACK.quarterMinutes,
        });
      })
      .catch(() => { if (alive) setWork(DAY_WORK_FALLBACK); });
    return () => { alive = false; };
  }, [date, enabled]);
  return work;
}

/** 칩 하나 — 고르면 시작·종료(·하루종일)가 이렇게 된다. */
export interface PresetChip {
  key: string;
  label: string;
  on: boolean;
  /** 하루종일로 바꾸는 칩(연차). */
  allDay?: boolean;
  startAt?: string;
  endAt?: string;
  /** 휴가 반차·반반차 — 고르면 종료가 잠긴다. */
  vacPart?: VacationPart | null;
}

const split = (v: string) => { const [d = '', t = '00:00'] = v.split('T'); return { d, m: minOf(t.slice(0, 5)) }; };
const join = (d: string, m: number) => `${d}T${hhmmOf(m)}`;
/** 'YYYY-MM-DDTHH:mm' 에 분을 더한다(날짜 넘김 포함). */
function addMinutes(v: string, add: number): string {
  const { d, m } = split(v);
  const [y, mo, day] = d.split('-').map(Number);
  const t = new Date(y!, (mo ?? 1) - 1, day ?? 1, 0, m + add);
  return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}T${pad2(t.getHours())}:${pad2(t.getMinutes())}`;
}
const minutesBetween = (a: string, b: string) => Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000);

/** 120 → 「2시간」 · 90 → 「1시간 30분」 · 30 → 「30분」. */
export function lengthLabel(min: number): string {
  const h = Math.floor(min / 60), m = min % 60;
  if (h && m) return tCalendar('calendar.preset.len_hm', { h, m });
  if (h) return tCalendar('calendar.preset.len_h', { h });
  return tCalendar('calendar.preset.len_m', { m });
}
/** 13:00 → 「1:00」 — 칩 안 짧은 시각(오전/오후 말은 칩 이름이 한다). */
const shortClock = (m: number) => `${displayHour12(Math.floor(m / 60))}:${pad2(m % 60)}`;

/** 유형별 추천 칩. 추천이 없는 유형이면 null. */
export function presetChips(
  type: CalendarEventType,
  ctx: { startAt: string; endAt: string; isAllDay: boolean; work: DayWork; vacPart: VacationPart | null },
): { label: string; chips: PresetChip[] } | null {
  const { startAt, endAt, isAllDay, work } = ctx;
  const s = split(startAt);
  const sameDay = split(endAt).d === s.d;
  const len = minutesBetween(startAt, endAt);

  if (type === 'meeting') {
    return {
      label: tCalendar('calendar.preset.label'),
      chips: MEETING_LENGTHS.map((d) => ({
        key: `m${d}`,
        label: lengthLabel(d),
        on: !isAllDay && len === d,
        startAt: join(s.d, s.m),
        endAt: addMinutes(join(s.d, s.m), d),
        vacPart: null,
      })),
    };
  }

  if (type === 'external') {
    const ws = minOf(work.start), we = minOf(work.end);
    const chips: PresetChip[] = [];
    const amEnd = Math.min(NOON, we);
    if (ws < amEnd) {
      chips.push({
        key: 'am', label: tCalendar('calendar.preset.am', { from: shortClock(ws), to: shortClock(amEnd) }),
        on: !isAllDay && sameDay && s.m === ws && split(endAt).m === amEnd,
        startAt: join(s.d, ws), endAt: join(s.d, amEnd), vacPart: null,
      });
    }
    const pmStart = Math.max(AFTER_LUNCH, ws);
    if (pmStart < we) {
      chips.push({
        key: 'pm', label: tCalendar('calendar.preset.pm', { from: shortClock(pmStart), to: shortClock(we) }),
        on: !isAllDay && sameDay && s.m === pmStart && split(endAt).m === we,
        startAt: join(s.d, pmStart), endAt: join(s.d, we), vacPart: null,
      });
    }
    return chips.length ? { label: tCalendar('calendar.preset.label'), chips } : null;
  }

  if (type === 'vacation') {
    const lo = minOf(work.earliestStart), hi = minOf(work.latestEnd);
    const part = (kind: VacationPart, mins: number): PresetChip => {
      // 시작은 지금 시각을 지키되 근무시간 안으로(출근 ≤ 시작 · 시작 + 길이 ≤ 퇴근). 하루종일이었으면 출근부터.
      const want = isAllDay ? lo : s.m;
      const start = Math.max(lo, Math.min(want, hi - mins));
      return {
        key: kind,
        label: tCalendar(kind === 'half' ? 'calendar.preset.half' : 'calendar.preset.quarter', { len: lengthLabel(mins) }),
        on: !isAllDay && ctx.vacPart === kind,
        startAt: join(s.d, start),
        endAt: addMinutes(join(s.d, start), mins),
        vacPart: kind,
      };
    };
    return {
      label: tCalendar('calendar.preset.kind'),
      chips: [
        part('half', work.halfMinutes),
        part('quarter', work.quarterMinutes),
        { key: 'full', label: tCalendar('calendar.preset.full'), on: isAllDay, allDay: true, vacPart: null },
      ],
    };
  }
  return null;
}

/** 수정 창을 열 때 — 휴가 일정의 길이가 반차·반반차와 같으면 그 종류로 본다(종료 잠금). */
export function inferVacationPart(startAt: string, endAt: string, isAllDay: boolean, work: DayWork = DAY_WORK_FALLBACK): VacationPart | null {
  if (isAllDay) return null;
  const len = minutesBetween(startAt, endAt);
  if (len === work.halfMinutes) return 'half';
  if (len === work.quarterMinutes) return 'quarter';
  return null;
}
